import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Form, Modal, Badge, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaShieldAlt, FaPuzzlePiece, FaWrench, FaInfoCircle, FaUndo } from 'react-icons/fa';
import api from '../axiosSetup';
import Swal from 'sweetalert2';
import CustomNavbar from '../CustomNavbar';
import cfg from '../../config';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

const PROTECTED_KEYS = new Set(['Wartung', 'WartungMessage', 'AppName', 'Version']);

// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const MODULE_INFO = () => ({
    Modul_Veranstaltungen: { label: 'Veranstaltungen', desc: `${cfg.eventTermPlural}, Kalender, Buchungen, Tauschbörse`, color: '#4e9af1' },
    Modul_Haes:            { label: cfg.costumeTermPlural, desc: `Verwaltung, Anträge, ${cfg.costumeTerm}typen`, color: '#f1a24e' },
    Modul_Arbeitseinsatz:  { label: 'Arbeitseinsatz',  desc: `Arbeitseinsätze für ${cfg.memberTermPlural} und ${cfg.adminRoleName}`, color: '#4ef16a' },
    Modul_Berichte:        { label: 'Berichte',        desc: 'Statistiken, Jahresbericht, Personalakten', color: '#c44ef1' },
});

export default function AdminConfig() {
    const [entries, setEntries] = useState([]);
    const [umgebung, setUmgebung] = useState([]);
    const [mailTest, setMailTest] = useState({ an: '', laeuft: false, ergebnis: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editModal, setEditModal] = useState({ show: false, entry: null, value: '' });
    const [addModal, setAddModal] = useState({ show: false, name: '', value: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [toggling, setToggling] = useState(null);
    const [resetting, setResetting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await api.get('/api/admin/config');
            // Die Schnittstelle liefert seit der Umstellung ein Objekt mit
            // Beschreibungen. Das Array bleibt als Rueckfall stehen, damit
            // ein aelterer Server die Seite nicht leer laesst.
            setEntries(Array.isArray(data) ? data : (data.eintraege || []));
            setUmgebung(Array.isArray(data) ? [] : (data.umgebung || []));
        } catch {
            setError('Konfiguration konnte nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleModule = async (entry) => {
        const newVal = entry.value === '1' ? '0' : '1';
        setToggling(entry.id);
        try {
            await api.put(`/api/admin/config/${entry.id}`, { value: newVal });
            setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, value: newVal } : e));
        } catch (err) {
            Swal.fire('Fehler', err.response?.data?.message || 'Speichern fehlgeschlagen', 'error');
        } finally {
            setToggling(null);
        }
    };

    const openEdit = (entry) => setEditModal({ show: true, entry, value: entry.value ?? '' });
    const closeEdit = () => setEditModal({ show: false, entry: null, value: '' });

    const saveEdit = async () => {
        setSaving(true);
        try {
            await api.put(`/api/admin/config/${editModal.entry.id}`, { value: editModal.value });
            closeEdit();
            load();
        } catch (err) {
            Swal.fire('Fehler', err.response?.data?.message || 'Speichern fehlgeschlagen', 'error');
        } finally {
            setSaving(false);
        }
    };

    const saveAdd = async () => {
        if (!addModal.name.trim()) return;
        setSaving(true);
        try {
            await api.post('/api/admin/config', {
                name: addModal.name.trim(),
                value: addModal.value,
                description: addModal.description
            });
            setAddModal({ show: false, name: '', value: '', description: '' });
            load();
        } catch (err) {
            Swal.fire('Fehler', err.response?.data?.message || 'Erstellen fehlgeschlagen', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (entry) => {
        const result = await Swal.fire({
            title: `"${entry.name}" löschen?`,
            text: 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });
        if (!result.isConfirmed) return;
        try {
            await api.delete(`/api/admin/config/${entry.id}`);
            load();
        } catch (err) {
            Swal.fire('Fehler', err.response?.data?.message || 'Löschen fehlgeschlagen', 'error');
        }
    };

    const handleDemoReset = async () => {
        const result = await Swal.fire({
            title: 'Demo-Daten zurücksetzen?',
            html: 'Alle Daten werden auf den ursprünglichen Demo-Stand zurückgesetzt.<br><br><strong>Dieser Vorgang kann nicht rückgängig gemacht werden.</strong>',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Zurücksetzen',
            cancelButtonText: 'Abbrechen',
            background: '#1a1a1a', color: '#fff',
        });
        if (!result.isConfirmed) return;
        setResetting(true);
        try {
            await api.post('/api/admin/demo-reset');
            await Swal.fire({
                title: 'Zurückgesetzt',
                text: 'Die Demo-Daten wurden erfolgreich wiederhergestellt. Du wirst jetzt ausgeloggt.',
                icon: 'success',
                background: '#1a1a1a', color: '#fff',
                confirmButtonText: 'OK',
            });
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
        } catch (err) {
            Swal.fire('Fehler', err.response?.data?.message || 'Reset fehlgeschlagen.', 'error');
        } finally {
            setResetting(false);
        }
    };

    const modules = entries.filter(e => e.name.startsWith('Modul_'));
    const systemEntries = entries.filter(e => !e.name.startsWith('Modul_'));

    const sendeMailTest = async () => {
        setMailTest(m => ({ ...m, laeuft: true, ergebnis: null }));
        try {
            const { data } = await api.post('/api/admin/config/mail-test', { an: mailTest.an });
            setMailTest(m => ({ ...m, laeuft: false, ergebnis: { ok: true, text: data.message } }));
        } catch (err) {
            setMailTest(m => ({
                ...m, laeuft: false,
                ergebnis: {
                    ok: false,
                    text: err.response?.data?.message || 'Der Versand ist fehlgeschlagen.',
                    grund: err.response?.data?.grund,
                },
            }));
        }
    };

    if (loading) return (
        <div className="text-center py-5"><Spinner animation="border" variant="light" /></div>
    );

    return (
        <>
        <CustomNavbar />
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-white mb-0">App-Konfiguration</h2>
                {IS_DEMO && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDemoReset}
                        disabled={resetting}
                        style={{ borderRadius: '0.5rem', fontWeight: 600 }}
                    >
                        {resetting
                            ? <><Spinner size="sm" animation="border" className="me-2" />Wird zurückgesetzt…</>
                            : <><FaUndo className="me-2" />Demo zurücksetzen</>
                        }
                    </Button>
                )}
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* ── Postausgang ausprobieren ── */}
            <Card className="mb-4" style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0.875rem',
            }}>
                <Card.Body>
                    <h5 className="text-light mb-2">Postausgang ausprobieren</h5>
                    <p className="text-secondary small mb-3">
                        Schickt eine Testnachricht mit den Werten aus dem Abschnitt
                        „E-Mail-Versand". So siehst du ohne Zugriff auf den Server, ob
                        die Zugangsdaten stimmen.
                    </p>
                    <Row className="g-2 align-items-center">
                        <Col xs={12} md>
                            <Form.Control type="email" placeholder="Empfängeradresse"
                                value={mailTest.an}
                                onChange={e => setMailTest(m => ({ ...m, an: e.target.value }))} />
                        </Col>
                        <Col xs="auto">
                            <Button variant="outline-info" onClick={sendeMailTest}
                                disabled={mailTest.laeuft || !mailTest.an.trim()}>
                                {mailTest.laeuft
                                    ? <><Spinner size="sm" animation="border" className="me-2" />Sende…</>
                                    : 'Testnachricht senden'}
                            </Button>
                        </Col>
                    </Row>
                    {mailTest.ergebnis && (
                        <Alert variant={mailTest.ergebnis.ok ? 'success' : 'danger'} className="mt-3 mb-0">
                            {mailTest.ergebnis.text}
                            {mailTest.ergebnis.grund && (
                                <div className="small mt-1" style={{ opacity: 0.8 }}>
                                    Meldung des Servers: {mailTest.ergebnis.grund}
                                </div>
                            )}
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* ── Was in der .env bleibt ── */}
            {umgebung.length > 0 && (
                <Card className="mb-5" style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0.875rem',
                }}>
                    <Card.Body>
                        <h5 className="text-light mb-2">
                            <FaShieldAlt className="me-2 text-warning" />In der Datei hinterlegt
                        </h5>
                        <p className="text-secondary small mb-3">
                            Diese Werte stehen in der Datei <code>.env</code> auf dem Server und
                            lassen sich hier bewusst nicht ändern: Geheimnisse gehören nicht in
                            die Datenbank, und Startwerte werden gebraucht, bevor eine
                            Datenbankverbindung steht. Angezeigt wird nur, <em>ob</em> etwas
                            hinterlegt ist — nie der Wert.
                        </p>
                        <Table variant="dark" size="sm" responsive className="mb-0">
                            <thead>
                                <tr>
                                    <th>Wert</th><th>Art</th><th>Stand</th><th>Bedeutung</th>
                                </tr>
                            </thead>
                            <tbody>
                                {umgebung.map(e => (
                                    <tr key={e.name}>
                                        <td>
                                            {e.beschriftung}
                                            <div className="text-secondary" style={{ fontSize: '.75rem' }}>
                                                <code>{e.name}</code>
                                            </div>
                                        </td>
                                        <td><Badge bg="secondary">{e.klasse}</Badge></td>
                                        <td>
                                            {e.hinterlegt
                                                ? <Badge bg="success">hinterlegt</Badge>
                                                : <Badge bg="danger">fehlt</Badge>}
                                        </td>
                                        <td className="text-secondary small">{e.hinweis}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* ── Module-Toggles ── */}
            <h5 className="text-light mb-3">
                <FaPuzzlePiece className="me-2 text-info" />Module
            </h5>
            <Row className="g-3 mb-5">
                {modules.map(entry => {
                    const info = MODULE_INFO()[entry.name] || { label: entry.name.replace('Modul_', ''), desc: entry.description, color: '#aaa' };
                    const isActive = entry.value === '1';
                    return (
                        <Col key={entry.id} xs={12} sm={6} lg={3}>
                            <Card style={{
                                background: isActive ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                                border: `1px solid ${isActive ? info.color + '55' : 'rgba(255,255,255,0.08)'}`,
                                borderRadius: '0.875rem',
                                transition: 'all 0.2s',
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <div style={{
                                                width: 10, height: 10, borderRadius: '50%',
                                                background: isActive ? info.color : '#555',
                                                display: 'inline-block', marginRight: 8
                                            }} />
                                            <span className="text-white fw-semibold">{info.label}</span>
                                        </div>
                                        <Form.Check
                                            type="switch"
                                            checked={isActive}
                                            onChange={() => toggleModule(entry)}
                                            disabled={toggling === entry.id}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </div>
                                    <p className="text-muted small mb-0" style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                                        {info.desc}
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* ── System-Einstellungen ── */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="text-light mb-0">
                    <FaWrench className="me-2 text-warning" />System-Einstellungen
                </h5>
                <Button variant="success" size="sm" onClick={() => setAddModal({ show: true, name: '', value: '', description: '' })}>
                    <FaPlus className="me-1" /> Neuer Eintrag
                </Button>
            </div>

            <Table responsive bordered variant="dark" className="align-middle">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Wert</th>
                        <th>Beschreibung</th>
                        <th style={{ width: 110 }}>Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    {systemEntries.map(entry => (
                        <tr key={entry.id}>
                            <td>
                                {PROTECTED_KEYS.has(entry.name) && (
                                    <FaShieldAlt className="text-warning me-1" title="Geschützt" />
                                )}
                                <code className="text-info">{entry.name}</code>
                            </td>
                            <td>
                                {entry.name === 'Wartung' ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check
                                            type="switch"
                                            checked={entry.value === '1'}
                                            onChange={() => toggleModule(entry)}
                                            disabled={toggling === entry.id}
                                        />
                                        <Badge bg={entry.value === '1' ? 'danger' : 'success'}>
                                            {entry.value === '1' ? 'Wartungsmodus AKTIV' : 'Normal'}
                                        </Badge>
                                    </div>
                                ) : (
                                    <span className="text-light">{entry.value || <span className="text-muted fst-italic">leer</span>}</span>
                                )}
                            </td>
                            <td className="text-muted small">{entry.description || '–'}</td>
                            <td>
                                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(entry)} title="Bearbeiten" aria-label="Konfiguration bearbeiten">
                                    <FaEdit />
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={PROTECTED_KEYS.has(entry.name)}
                                    title={PROTECTED_KEYS.has(entry.name) ? 'Geschützt – kann nicht gelöscht werden' : 'Löschen'}
                                    aria-label="Konfiguration löschen"
                                    onClick={() => handleDelete(entry)}
                                >
                                    <FaTrash />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/* Bearbeiten-Dialog als eigenes Overlay.
                Das Bootstrap-Modal war nachweislich im DOM, sichtbar geschaltet
                und korrekt positioniert — blieb aber unsichtbar. Statt weiter zu
                raten steht hier ein Overlay mit direkten Stilangaben, das von
                keiner Bibliotheksvoreinstellung abhängt. */}
            {editModal.show && (
                <div
                    onClick={closeEdit}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 99999,
                        background: 'rgba(0,0,0,0.75)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '1rem',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: '100%', maxWidth: 520,
                            background: '#212529',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: 14,
                            boxShadow: '0 24px 70px rgba(0,0,0,0.8)',
                            color: '#fff',
                            overflow: 'hidden',
                        }}
                    >
                        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ fontWeight: 600 }}>
                                Wert ändern: <code style={{ color: '#ffcb57' }}>{editModal.entry?.name}</code>
                            </span>
                            <button onClick={closeEdit} aria-label="Schließen"
                                style={{ background: 'none', border: 0, color: '#9aa0a6', fontSize: '1.4rem', lineHeight: 1, cursor: 'pointer' }}>×</button>
                        </div>

                        <div style={{ padding: '1.25rem' }}>
                            {editModal.entry?.description && (
                                <p style={{ color: '#9aa0a6', fontSize: '0.85rem', marginBottom: '0.9rem' }}>
                                    <FaInfoCircle className="me-1" />{editModal.entry.description}
                                </p>
                            )}
                            <input
                                autoFocus
                                value={editModal.value}
                                onChange={e => setEditModal(p => ({ ...p, value: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') closeEdit(); }}
                                placeholder="Wert eingeben…"
                                style={{
                                    width: '100%', padding: '0.55rem 0.8rem',
                                    background: '#16181b', color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
                                    fontSize: '0.95rem',
                                }}
                            />
                        </div>

                        <div style={{ padding: '0.9rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <Button variant="secondary" size="sm" onClick={closeEdit}>Abbrechen</Button>
                            <Button variant="warning" size="sm" onClick={saveEdit} disabled={saving}>
                                {saving ? <Spinner size="sm" animation="border" /> : 'Speichern'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alter Dialog — bleibt vorerst deaktiviert */}
            <Modal show={false} onHide={closeEdit} centered>
                <Modal.Header closeButton className="bg-dark text-white border-secondary">
                    <Modal.Title>Wert ändern: <code>{editModal.entry?.name}</code></Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    {editModal.entry?.description && (
                        <p className="text-muted small mb-3">
                            <FaInfoCircle className="me-1" />{editModal.entry.description}
                        </p>
                    )}
                    <Form.Control
                        value={editModal.value}
                        onChange={e => setEditModal(p => ({ ...p, value: e.target.value }))}
                        className="bg-dark text-white border-secondary"
                        placeholder="Wert eingeben..."
                    />
                </Modal.Body>
                <Modal.Footer className="bg-dark border-secondary">
                    <Button variant="secondary" onClick={closeEdit}>Abbrechen</Button>
                    <Button variant="primary" onClick={saveEdit} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Speichern'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Modal */}
            <Modal show={addModal.show} onHide={() => setAddModal({ show: false, name: '', value: '', description: '' })} centered>
                <Modal.Header closeButton className="bg-dark text-white border-secondary">
                    <Modal.Title>Neuer Konfigurationseintrag</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            value={addModal.name}
                            onChange={e => setAddModal(p => ({ ...p, name: e.target.value }))}
                            className="bg-dark text-white border-secondary"
                            placeholder="z.B. Modul_Fotos"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Wert</Form.Label>
                        <Form.Control
                            value={addModal.value}
                            onChange={e => setAddModal(p => ({ ...p, value: e.target.value }))}
                            className="bg-dark text-white border-secondary"
                            placeholder="z.B. 1"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Beschreibung <span className="text-muted">(optional)</span></Form.Label>
                        <Form.Control
                            value={addModal.description}
                            onChange={e => setAddModal(p => ({ ...p, description: e.target.value }))}
                            className="bg-dark text-white border-secondary"
                            placeholder="Was steuert dieser Wert?"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-dark border-secondary">
                    <Button variant="secondary" onClick={() => setAddModal({ show: false, name: '', value: '', description: '' })}>Abbrechen</Button>
                    <Button variant="success" onClick={saveAdd} disabled={saving || !addModal.name.trim()}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Erstellen'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
        </>
    );
}
